/**
 * A FILA DO ESPELHO: o e-mail que o órgão manda em cópia e que o HUB só PALPITOU de qual contrato é (backend #580/#581).
 * Prévia aprovada pelo Heitor em 31/10/2026 (L1 G1 A1): https://claude.ai/artifact/21RrjLfSGmrCZmEeTm5eck
 *
 * O e-mail com certeza (número do ofício, resposta a e-mail já confirmado) vira evento sozinho e não passa por aqui. A
 * tela só agrupa pela força do palpite e propõe o contrato e a anualidade; quem decide é o clique.
 */

export type FormaDoCasamento = 'oficio' | 'thread' | 'manual' | 'contrato' | 'endereco' | 'dominio';
export type MotivoDoPalpite = 'nenhuma_chave' | 'oficios_divergentes' | 'contrato_ambiguo';

export interface ContratoCandidato { id: number; contractNumber: string; clientName: string }

export interface EmailDoEspelho {
  id: number;
  subject: string | null;
  fromEmail: string;
  fromName: string | null;
  direction: 'enviado' | 'recebido';
  emailDate: string | null;
  createdAt?: string;
  clientId: number | null;
  contractId: number | null;
  matchedBy: FormaDoCasamento | null;
  attachments?: Array<{ filename: string; contentType?: string; size?: number; s3Key?: string }>;
  motivoDoPalpite?: MotivoDoPalpite | null;
  oficiosCitados?: string[];
  contratosCandidatos?: ContratoCandidato[];
}

/** G1: os grupos, do palpite mais forte ao mais fraco, e por último o que não casou com contrato nenhum. */
export type GrupoDoEspelho = FormaDoCasamento | 'semContrato';
export const ORDEM_DO_ESPELHO: GrupoDoEspelho[] = ['oficio', 'thread', 'manual', 'contrato', 'endereco', 'dominio', 'semContrato'];

export const GRUPO_DO_ESPELHO: Record<GrupoDoEspelho, { titulo: string; sub: string }> = {
  oficio: { titulo: 'Pelo número do ofício', sub: 'O texto cita um OF-AAAA/NNN. Normalmente entra sozinho; está aqui porque ainda não virou registro.' },
  thread: { titulo: 'Pela conversa', sub: 'Responde a um e-mail que já estava arquivado. Chega aqui quando o e-mail de antes também era palpite.' },
  manual: { titulo: 'Corrigido à mão', sub: 'Alguém já escolheu o contrato. Falta confirmar.' },
  contrato: { titulo: 'Pelo número do contrato', sub: 'O texto cita um número de contrato. É palpite: poucos contratos têm número, e "07/2025" também é o jeito de escrever uma competência.' },
  endereco: { titulo: 'Pelo endereço do cliente', sub: 'O remetente é um e-mail do cliente, e o contrato escolhido é o único ativo dele.' },
  dominio: { titulo: 'Só pelo domínio', sub: 'O palpite mais fraco: só o domínio do remetente bateu com o cliente. É o que mais pede correção.' },
  semContrato: { titulo: 'Sem contrato nenhum', sub: 'Não bateu com cliente nenhum. Escolha o contrato da tratativa, ou diga que não é tratativa.' },
};

export function grupoDoEmail(e: Pick<EmailDoEspelho, 'contractId' | 'matchedBy'>): GrupoDoEspelho {
  if (!e.contractId || !e.matchedBy) return 'semContrato';
  return ORDEM_DO_ESPELHO.includes(e.matchedBy) ? e.matchedBy : 'dominio';
}

const instante = (e: EmailDoEspelho) => String(e.emailDate || e.createdAt || '');

/** Agrupa na ordem G1. Dentro do grupo, do mais novo ao mais antigo. Um e-mail que vier nas duas listas aparece uma vez. */
export function agruparEspelho(sugestoes: EmailDoEspelho[], semContrato: EmailDoEspelho[]) {
  const vistos = new Set<number>();
  const todos = [...sugestoes, ...semContrato].filter((e) => (vistos.has(e.id) ? false : (vistos.add(e.id), true)));
  return ORDEM_DO_ESPELHO
    .map((grupo) => ({
      grupo,
      emails: todos.filter((e) => grupoDoEmail(e) === grupo).sort((a, b) => instante(b).localeCompare(instante(a))),
    }))
    .filter((g) => g.emails.length > 0);
}

/** O selo do motivo do palpite, quando o servidor mandou um. Código desconhecido aparece com o próprio nome. */
export function seloDoMotivo(e: Pick<EmailDoEspelho, 'motivoDoPalpite' | 'oficiosCitados'>): string | null {
  switch (e.motivoDoPalpite) {
    case 'nenhuma_chave': return 'nenhuma chave';
    case 'contrato_ambiguo': return 'contrato ambíguo';
    case 'oficios_divergentes': {
      const of = (e.oficiosCitados || []).filter(Boolean);
      return of.length ? `ofícios divergentes: ${of.join(', ')}` : 'ofícios divergentes';
    }
    case null: case undefined: return null;
    default: return String(e.motivoDoPalpite);
  }
}

/**
 * O contrato que vem escolhido. ⛔ Contrato AMBÍGUO (o número casou com mais de um) não vem escolhido: o palpite do
 * servidor seria um deles ao acaso, e confirmar sem olhar prenderia o e-mail no órgão errado. Sem contrato, idem.
 */
export function contratoProposto(e: Pick<EmailDoEspelho, 'contractId' | 'motivoDoPalpite' | 'contratosCandidatos'>): number | null {
  if (e.motivoDoPalpite === 'contrato_ambiguo' && (e.contratosCandidatos || []).length > 1) return null;
  return e.contractId || null;
}

const VIVAS = new Set(['devida', 'pedida', 'deferida']);

/**
 * A1: a anualidade que vem escolhida. Exatamente UMA viva (devida, pedida ou deferida) no contrato → ela. Nenhuma ou
 * mais de uma → `null`, a linha do tempo do contrato (a anualidade é opcional, decisão 4A).
 */
export function anualidadeProposta(contractId: number | null, anualidades: Array<{ id: number; contractId: number | string; status: string }>): number | null {
  if (!contractId) return null;
  const vivas = anualidades.filter((a) => Number(a.contractId) === Number(contractId) && VIVAS.has(a.status));
  return vivas.length === 1 ? vivas[0].id : null;
}
